<script lang="ts">
	import '../app.css';
	import { afterNavigate } from '$app/navigation';
	import { browser } from '$app/environment';
	import { onMount } from 'svelte';
	import EkaviLockup from '$lib/components/EkaviLockup.svelte';
	import ConsentBanner from '$lib/components/ConsentBanner.svelte';
	import {
		initGA,
		captureUtmParams,
		trackPageView,
		enableOutboundTracking,
		trackCtaClick
	} from '$lib/analytics';
	import { SITE } from '$lib/site';

	let { children } = $props();

	// Analytics: no-op in dev / when GA isn't configured.
	initGA();
	captureUtmParams();

	afterNavigate(() => {
		if (browser) trackPageView(window.location.pathname, document.title);
	});

	onMount(() => enableOutboundTracking());
</script>

<a class="skip-link" href="#main">Skip to main content</a>

<header class="topbar">
	<div class="topbar-inner">
		<EkaviLockup height={28} />
		<div class="topbar-right">
			<nav class="topbar-nav" aria-label="Primary">
				<a
					href="/#what-it-does"
					class="nav-link"
					onclick={() => trackCtaClick('how it works', 'topbar', '/#what-it-does')}>How it works</a
				>
				<a
					href="/#stack"
					class="nav-link"
					onclick={() => trackCtaClick('stack', 'topbar', '/#stack')}>Stack</a
				>
				<a
					href="/trust/"
					class="nav-link"
					onclick={() => trackCtaClick('trust', 'topbar', '/trust/')}>Trust</a
				>
			</nav>
			<a
				href="/#design-partners"
				class="nav-cta"
				onclick={() => trackCtaClick('join', 'topbar', '/#design-partners')}
			>
				Join
				<span class="cta-arrow" aria-hidden="true">→</span>
			</a>
		</div>
	</div>
</header>

<main id="main">
	{@render children()}
</main>

<footer class="site-footer">
	<div class="container-wide">
		<hr class="footer-rule" />
		<div class="footer-row">
			<p class="footer-strip">
				{SITE.name} is built for independent practices. PHI does not train our models.
			</p>
			<nav class="footer-nav" aria-label="Site">
				<a href="/trust/">Trust</a>
				<a href="/privacy/">Privacy</a>
				<a href="/hipaa/">HIPAA</a>
			</nav>
		</div>
		<p class="footer-meta">
			<span>{SITE.name}</span>
			<span class="sep">·</span>
			<span>{new Date().getFullYear()}</span>
		</p>
	</div>
</footer>

<ConsentBanner />

<style>
	.skip-link {
		position: absolute;
		top: var(--ekavi-space-2);
		left: var(--ekavi-space-2);
		padding: var(--ekavi-space-2) var(--ekavi-space-4);
		background: var(--ekavi-charcoal);
		color: var(--ekavi-bone);
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-14);
		font-weight: var(--ekavi-weight-halbfett);
		border: none;
		transform: translateY(-200%);
		transition: transform var(--ekavi-duration-quick) var(--ekavi-ease-out);
		z-index: 100;
	}
	.skip-link:focus-visible {
		transform: translateY(0);
		outline: 1px solid var(--ekavi-ochre);
		outline-offset: 3px;
	}

	.topbar {
		position: sticky;
		top: 0;
		z-index: 40;
		background: color-mix(in srgb, var(--ekavi-bone) 92%, transparent);
		backdrop-filter: blur(8px);
		-webkit-backdrop-filter: blur(8px);
		border-bottom: var(--ekavi-stroke-hairline);
	}
	.topbar-inner {
		max-width: var(--ekavi-shell-max);
		margin: 0 auto;
		padding: 14px var(--ekavi-space-6);
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--ekavi-space-6);
	}
	.topbar-right {
		display: inline-flex;
		align-items: center;
		gap: var(--ekavi-space-9); /* 36px breath between nav cluster and CTA */
		min-width: 0;
	}
	.topbar-nav {
		display: inline-flex;
		align-items: center;
		gap: 28px; /* roomy, deliberate spacing between menu items */
	}
	.nav-link {
		position: relative;
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-14);
		font-weight: var(--ekavi-weight-buch);
		letter-spacing: -0.005em;
		color: var(--ekavi-ink-2);
		text-decoration: none;
		border-bottom: none;
		padding: var(--ekavi-space-2) 0;
		white-space: nowrap;
		transition: color var(--ekavi-duration-quick) var(--ekavi-ease-out);
	}
	.nav-link::after {
		content: '';
		position: absolute;
		left: 0;
		right: 0;
		bottom: 2px;
		height: 1px;
		background: currentColor;
		transform: scaleX(0);
		transform-origin: left center;
		transition: transform var(--ekavi-duration-quick) var(--ekavi-ease-out);
	}
	.nav-link:hover {
		color: var(--ekavi-charcoal);
	}
	.nav-link:hover::after {
		transform: scaleX(1);
	}
	.nav-link:focus-visible {
		outline: 1px solid var(--ekavi-ochre);
		outline-offset: 4px;
		color: var(--ekavi-charcoal);
	}

	/* Persistent header CTA — sharp charcoal block, brand-aligned (no
	   border-radius — Ekavi uses square corners across the system). */
	.nav-cta {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		height: 36px;
		padding: 0 18px;
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-14);
		font-weight: var(--ekavi-weight-halbfett);
		letter-spacing: 0.005em;
		color: var(--ekavi-bone);
		background: var(--ekavi-charcoal);
		border: 1px solid var(--ekavi-charcoal);
		text-decoration: none;
		white-space: nowrap;
		transition:
			background var(--ekavi-duration-quick) var(--ekavi-ease-out),
			transform var(--ekavi-duration-base) var(--ekavi-ease-out);
	}
	.nav-cta:hover,
	.nav-cta:focus-visible {
		color: var(--ekavi-bone);
		background: #000;
		transform: translateY(-2px);
	}
	.nav-cta:active {
		transform: translateY(1px);
	}
	.nav-cta:focus-visible {
		outline: 1px solid var(--ekavi-ochre);
		outline-offset: 3px;
	}
	@media (prefers-reduced-motion: reduce) {
		.nav-cta {
			transition: background var(--ekavi-duration-quick) var(--ekavi-ease-out);
		}
		.nav-cta:hover,
		.nav-cta:focus-visible,
		.nav-cta:active {
			transform: none;
		}
	}

	@media (max-width: 860px) {
		.topbar-nav {
			display: none;
		}
		.topbar-right {
			gap: var(--ekavi-space-4);
		}
	}
	@media (max-width: 480px) {
		.topbar-inner {
			padding: 12px var(--ekavi-space-4);
			gap: var(--ekavi-space-3);
		}
	}

	.site-footer {
		padding: var(--ekavi-space-12) 0 var(--ekavi-space-9);
		margin-top: var(--ekavi-space-24);
	}
	.footer-rule {
		border: none;
		border-top: var(--ekavi-stroke-hairline);
		margin: 0 0 var(--ekavi-space-6);
	}
	.footer-row {
		display: flex;
		flex-wrap: wrap;
		gap: var(--ekavi-space-6);
		align-items: baseline;
		justify-content: space-between;
	}
	.footer-strip {
		margin: 0;
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-14);
		color: var(--ekavi-ink-2);
		max-width: 56ch;
		line-height: var(--ekavi-leading-body);
	}
	.footer-nav {
		display: inline-flex;
		gap: var(--ekavi-space-6);
	}
	.footer-nav a {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		letter-spacing: var(--ekavi-track-caps);
		text-transform: uppercase;
		color: var(--ekavi-ink-2);
		border-bottom-color: transparent;
	}
	.footer-nav a:hover {
		border-bottom-color: var(--ekavi-charcoal);
		color: var(--ekavi-charcoal);
	}
	.footer-meta {
		margin: var(--ekavi-space-6) 0 0;
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-3);
		font-variant-numeric: tabular-nums;
	}
	.sep {
		margin: 0 var(--ekavi-space-2);
		color: var(--ekavi-ink-4);
	}
</style>
